import { useEffect } from "react";
import { useLocation } from "react-router";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

/**
 * Cross-page hash-scroll for the dashboard shell.
 *
 * Mounted ONCE in AppShell so a deep-link (e.g. a command-palette result) that
 * navigates to `/page#section` from a DIFFERENT page scrolls the target into
 * view once the destination route mounts. `useAppSearch.handleNavigate` only
 * scrolls for same-page hits; React Router does not hash-scroll on navigate and
 * there is no other global handler, so cross-page deep-links otherwise land at
 * page top.
 *
 * No-op on non-scroll hashes: a hash with no matching element id (e.g. the
 * streaming flyout modes `#settings`/`#analytics`/`#logs`, which the flyout host
 * consumes and clears) resolves to null and is skipped — never throws, never
 * hijacks a page that manages its own hash/tab state.
 *
 * Lazy/async timing: on a cross-page nav the target may sit behind a
 * Suspense-lazy route or async-loaded data and not be in the DOM at first tick,
 * so we poll on a bounded rAF loop up to ~1s, stopping the moment the element
 * appears. Self-cleaning on unmount or hash change.
 */
export const useHashScroll = () => {
  const location = useLocation();
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    if (!id) return;

    const behavior: ScrollBehavior = prefersReducedMotion ? "auto" : "smooth";
    const deadline = performance.now() + 1000; // budget for lazy route + async mount
    let raf = 0;

    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior, block: "start" });
        return;
      }
      if (performance.now() < deadline) {
        raf = window.requestAnimationFrame(tick);
      }
    };

    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [location.hash, location.pathname, prefersReducedMotion]);
};
